import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { jsonApi } from '@/lib/jsonApi';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { templateCounts } from '@/components/taxonomy/counts';

const COLS = [
  { key: 'gap', title: 'Gaps', dot: 'bg-[#C4353D]' },
  { key: 'positive_driver', title: 'Positive Drivers', dot: 'bg-[#2E9A6B]' },
];

function AttributeBlock({ attr, index }) {
  return (
    <section className="border border-[#E2E8F0] rounded-xl p-4" data-testid={`template-preview-attribute-${index}`}>
      <p className="font-semibold text-[#0F172A] text-sm">{index + 1}. {attr.name}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-3">
        {COLS.map((c) => {
          const list = attr.factors.filter((f) => f.classification === c.key);
          return (
            <div key={c.key}>
              <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#64748B] mb-1.5 flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />{c.title} · {list.length}</p>
              {list.length ? (
                <ul className="space-y-1">{list.map((f) => <li key={f.factor_id} className="text-xs text-[#334155] leading-snug">{f.name}</li>)}</ul>
              ) : <p className="text-xs text-[#94A3B8]">None</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Read-only look at a master template before the one-time copy.
export default function TemplatePreview({ template, open, onOpenChange, onUse, canUse }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open || !template) return;
    setDetail(null); setError(null);
    jsonApi(`/taxonomy/templates/${template.template_id}`).then(setDetail).catch(setError);
  }, [open, template]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[860px] max-h-[88vh] flex flex-col p-0 gap-0" data-testid="template-preview-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#F1F5F9]">
          <DialogTitle className="font-['Montserrat'] text-xl" data-testid="template-preview-title">{template?.name} template</DialogTitle>
          <DialogDescription data-testid="template-preview-counts">{template ? templateCounts(template) : ''} · read-only preview</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-3" data-testid="template-preview-body">
          {!detail && !error && <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#94A3B8]" /></div>}
          {error && <p className="text-sm text-[#A1272E]">{error.message}</p>}
          {detail?.attributes.map((a, i) => <AttributeBlock key={a.attribute_id} attr={a} index={i} />)}
        </div>
        <div className="px-6 py-4 border-t border-[#F1F5F9] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-xs text-[#64748B]">Choosing it copies this list into the client’s own editable taxonomy. The master template never changes.</p>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="template-preview-close">Close</Button>
            {canUse && template?.available && (
              <Button onClick={() => onUse(template)} className="bg-[#1A3E6F] hover:bg-[#15325A]" data-testid="template-preview-use">Use {template.name}</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
